import React, { useState, useRef, useCallback } from 'react';
import { useData } from '../../context/DataContext';
import { useGeneSearch } from '../../hooks/useGeneSearch';

export default function ReportControls({
  reportGenes, setReportGenes,
  reportColorMode, setReportColorMode,
}) {
  const { data, resolveGene } = useData();
  const { query, setQuery, suggestions } = useGeneSearch();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [textareaValue, setTextareaValue] = useState('');
  const [invalidGenes, setInvalidGenes] = useState([]);
  const wrapperRef = useRef(null);

  // Parse a raw text input into resolved genes + invalid names
  const parseGenes = useCallback((text) => {
    const raw = text
      .split(/[\n,\t;]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0);

    const resolved = [];
    const invalid = [];
    const seen = new Set(reportGenes.map(g => g.toLowerCase()));

    for (const name of raw) {
      const gene = resolveGene(name);
      if (gene && !seen.has(gene.toLowerCase())) {
        resolved.push(gene);
        seen.add(gene.toLowerCase());
      } else if (!gene) {
        invalid.push(name);
      }
    }

    if (resolved.length > 0) {
      setReportGenes(prev => [...prev, ...resolved]);
    }
    setInvalidGenes(prev => [...prev, ...invalid]);
    setTextareaValue('');
  }, [reportGenes, resolveGene, setReportGenes]);

  const handleTextareaKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      parseGenes(textareaValue);
    }
  };

  const handleTextareaBlur = () => {
    if (textareaValue.trim()) {
      parseGenes(textareaValue);
    }
  };

  const removeGene = (gene) => {
    setReportGenes(prev => prev.filter(g => g !== gene));
  };

  const removeInvalid = (name) => {
    setInvalidGenes(prev => prev.filter(g => g !== name));
  };

  const handleAutocompleteSelect = (gene) => {
    if (!reportGenes.includes(gene)) {
      setReportGenes(prev => [...prev, gene]);
    }
    setQuery('');
    setShowSuggestions(false);
  };

  const addPanel = (panelName) => {
    const panel = data.panels[panelName];
    if (!panel) return;
    const existing = new Set(reportGenes.map(g => g.toLowerCase()));
    const newGenes = [];
    for (const geneList of Object.values(panel)) {
      for (const gene of geneList) {
        if (!existing.has(gene.toLowerCase())) {
          newGenes.push(gene);
          existing.add(gene.toLowerCase());
        }
      }
    }
    if (newGenes.length > 0) {
      setReportGenes(prev => [...prev, ...newGenes]);
    }
  };

  const clearAll = () => {
    setReportGenes([]);
    setInvalidGenes([]);
    setTextareaValue('');
  };

  const validCount = reportGenes.length;
  const invalidCount = invalidGenes.length;

  return (
    <div className="report-controls">
      <label>Paste Gene List</label>
      <textarea
        className="gene-textarea"
        value={textareaValue}
        onChange={e => setTextareaValue(e.target.value)}
        onKeyDown={handleTextareaKeyDown}
        onBlur={handleTextareaBlur}
        placeholder="Paste genes separated by commas, tabs, or newlines..."
        rows={4}
        spellCheck={false}
      />

      <div className="gene-search report-gene-add" ref={wrapperRef}>
        <label>Add Single Gene</label>
        <input
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setShowSuggestions(true); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder="Type gene name..."
          spellCheck={false}
          autoComplete="off"
        />
        {showSuggestions && suggestions.length > 0 && (
          <ul className="suggestions">
            {suggestions.map(({ gene, matchedAs }) => (
              <li key={gene} onMouseDown={() => handleAutocompleteSelect(gene)}>
                <span className="gene-name">{gene}</span>
                {matchedAs && <span className="alias-tag">({matchedAs})</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="quick-add-section">
        <label>Quick Add Panel</label>
        <div className="quick-add-buttons">
          {data && Object.keys(data.panels).map(name => (
            <button key={name} onClick={() => addPanel(name)}>
              + {name}
            </button>
          ))}
        </div>
      </div>

      {(validCount > 0 || invalidCount > 0) && (
        <div className="gene-tags-section">
          <div className="gene-count-row">
            <span className="gene-count-label">
              {validCount} gene{validCount !== 1 ? 's' : ''} selected
              {invalidCount > 0 && (
                <span className="invalid-count"> ({invalidCount} not found)</span>
              )}
            </span>
          </div>
          <div className="gene-tags">
            {reportGenes.map(gene => (
              <span key={gene} className="gene-tag">
                {gene}
                <button onClick={() => removeGene(gene)}>&times;</button>
              </span>
            ))}
            {invalidGenes.map(name => (
              <span key={name} className="gene-tag invalid">
                {name}
                <button onClick={() => removeInvalid(name)}>&times;</button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="color-toggle">
        <label>Color Mode</label>
        <div className="toggle-buttons">
          <button
            className={reportColorMode === 'relative' ? 'active' : ''}
            onClick={() => setReportColorMode('relative')}
          >
            Z-score
          </button>
          <button
            className={reportColorMode === 'absolute' ? 'active' : ''}
            onClick={() => setReportColorMode('absolute')}
          >
            Absolute
          </button>
        </div>
      </div>

      {validCount > 0 && (
        <div className="report-actions">
          <button className="clear-all-btn" onClick={clearAll}>
            Clear All
          </button>
        </div>
      )}
    </div>
  );
}
